import React, {useMemo} from 'react';
import {useIntl} from 'umi';
import {Button, Dropdown, Empty, Input, Segmented, Space, Spin} from 'antd';
import {
  CheckCircleOutlined,
  DownOutlined,
  EnvironmentOutlined,
  RightOutlined,
  SearchOutlined,
  ShopOutlined,
  UserOutlined,
} from '@ant-design/icons';
import classNames from 'classnames';

import GoogleAddressSearch from '@/components/AutoComplete/GooglePlaceAutocomplete';
import {ServiceTypeRender} from '@/components/Filters/ServiceTypeSelect';
import AgentName from '@/components/Agent/AgentName';
import {F, INTL_ACTION, INTL_ENTITY} from '@/utils/intl';
import {LeadModel} from '@/typings/models/Lead';

import {CascaderColumn, CascaderOption} from './CascaderColumn';
import {useBranchCascader} from './useBranchCascader';
import {Selection} from './utils';
import styles from './index.less';

type Props = {
  value?: Selection;
  initialValue?: { serviceId?: number | string; agentId?: number | string; branchId?: number | string };
  lead?: LeadModel;
  onChange?: (value: Selection) => void;
  disabled?: boolean;
};

const BranchServiceAgentCascader = ({ value, initialValue, lead, onChange, disabled = false }: Props) => {
  const intl = useIntl();

  const {
    branch,
    service,
    agent,
    sortedBranches,
    visibleServices,
    visibleAgents,
    searchType,
    setSearchType,
    searchTerm,
    setSearchTerm,
    dropdownOpen,
    setDropdownOpen,
    isAddressSearch,
    searchLoading,
    addressLoading,
    servicesLoading,
    selectBranch,
    selectService,
    selectAgent,
    onAddressSearch,
  } = useBranchCascader({ value, initialValue, lead, onChange: onChange ?? (() => {}) });

  const triggerLabel = useMemo(() => {
    if (!branch) {
      return intl.formatMessage({
        id: 'pages.calendar.chooseAgentOrService',
        defaultMessage: 'Choose a Service or an Agent',
      });
    }
    return (
      <Space>
        <Space className={styles.sectionSpace}>
          <EnvironmentOutlined />
          {branch.name}
        </Space>
        <RightOutlined />
        {service ? (
          <Space className={styles.sectionSpace}>
            <ShopOutlined />
            {service.service_type?.name}
          </Space>
        ) : (
          <F entity={INTL_ENTITY.SERVICE} action={INTL_ACTION.SELECT} defaultMessage="Select Service" />
        )}
        <RightOutlined />
        {agent ? (
          <Space className={styles.sectionSpace}>
            <UserOutlined />
            <span>
              {agent.firstname} {agent.lastname}
            </span>
          </Space>
        ) : (
          <F entity={INTL_ENTITY.AGENT} action={INTL_ACTION.SELECT} defaultMessage="Select Agent" />
        )}
      </Space>
    );
  }, [branch, service, agent, intl]);

  return (
    <div>
      <Dropdown
        open={dropdownOpen}
        onOpenChange={open => {
          if (!disabled) setDropdownOpen(open);
        }}
        trigger={['click']}
        dropdownRender={() => (
          <Spin spinning={searchLoading}>
            <div data-testid="cascader-dropdown" className={styles.dropdown}>
              <div className={styles.searchContainer}>
                <Segmented
                  options={[
                    { label: <SearchOutlined />, value: 'Name' },
                    { label: <EnvironmentOutlined />, value: 'Address' },
                  ]}
                  value={searchType}
                  onChange={v => setSearchType(v as 'Name' | 'Address')}
                />
                {isAddressSearch ? (
                  <GoogleAddressSearch onChange={onAddressSearch} />
                ) : (
                  <Input
                    prefix={<SearchOutlined />}
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    allowClear
                  />
                )}
              </div>

              <Spin spinning={addressLoading}>
                <div className={styles.selectionContainer}>
                  <CascaderColumn icon={<EnvironmentOutlined />} title={<F id="entity.branch.singular" defaultMessage="Branch" />}>
                    {sortedBranches.map(b => (
                      <CascaderOption
                        key={b.id}
                        testid="cascader-branch-option"
                        selected={branch?.id === b.id}
                        tooltip={b.address}
                        suffix={<RightOutlined />}
                        onClick={() => selectBranch(b)}
                      >
                        <div>{b.name}</div>
                        {isAddressSearch && b.distance && (
                          <small className={styles.distance}>{b.distance.toFixed(2)} KM</small>
                        )}
                      </CascaderOption>
                    ))}
                  </CascaderColumn>

                  <CascaderColumn
                    icon={<ShopOutlined />}
                    title={<F id="entity.service.singular" defaultMessage="Service" />}
                    loading={!!servicesLoading}
                  >
                    {visibleServices.map(svc => (
                      <CascaderOption
                        key={svc.id}
                        testid="cascader-service-option"
                        selected={service?.id === svc.id}
                        suffix={<RightOutlined />}
                        onClick={() => selectService(svc)}
                      >
                        <small>
                          <ServiceTypeRender breakRow serviceType={svc.service_type} />
                        </small>
                      </CascaderOption>
                    ))}
                  </CascaderColumn>

                  <CascaderColumn icon={<UserOutlined />} title={<F id="entity.agent.singular" defaultMessage="Agent" />}>
                    {visibleAgents.map(ag => {
                      const isSelected =
                        agent?.id === ag.id &&
                        Boolean(agent?.isWholeService) === Boolean(ag.isWholeService);
                      return (
                        <CascaderOption
                          key={`${ag.id}-${ag.isWholeService ? 'w' : 'a'}`}
                          selectedAgent={isSelected}
                          suffix={isSelected ? <CheckCircleOutlined /> : undefined}
                          onClick={() => selectAgent(ag)}
                        >
                          {ag.isWholeService ? ag.firstname : <AgentName agent={ag} />}
                        </CascaderOption>
                      );
                    })}
                  </CascaderColumn>
                </div>

                {sortedBranches.length === 0 && !addressLoading && !searchLoading && <Empty />}
              </Spin>
            </div>
          </Spin>
        )}
      >
        <Button
          data-testid="branch-service-agent-cascader-trigger"
          disabled={disabled}
          loading={addressLoading}
          className={classNames(styles.branchServiceAgentCascaderDropdown, styles.triggerButton, {
            [styles.placeholder]: !branch,
            [styles.disabled]: disabled,
          })}
          size="large"
        >
          <div className={styles.selectionPresentation}>{triggerLabel}</div>
          <DownOutlined />
        </Button>
      </Dropdown>
    </div>
  );
};

export default BranchServiceAgentCascader;
